import apiClient from './client';
import type { FeedbackType } from './feedback';

export interface ListFeedbackParams {
  pageNumber?: number;
  pageSize?: number;
  status?: 0 | 1;
  materialId?: string;
  agentId?: string;
  keyword?: string;
  startTime?: number;
  endTime?: number;
}

export interface RawAdminFeedbackItem {
  id: string;
  materialid: string;
  materialtitle: string;
  agentid: string;
  agentname?: string;
  agentemail?: string;
  content: string;
  feedbacktype: FeedbackType;
  status: number;
  submittime: number;
  processtime?: number | null;
  processby?: string;
  processbyname?: string;
  processremark?: string;
}

export interface AdminFeedbackItem {
  id: string;
  materialId: string;
  materialTitle: string;
  agentId: string;
  agentName?: string;
  agentEmail?: string;
  content: string;
  feedbackType: FeedbackType;
  status: number;
  submitTime: number;
  processTime?: number | null;
  processBy?: string;
  processByName?: string;
  processRemark?: string;
}

export function normalizeAdminFeedbackItem(raw: RawAdminFeedbackItem): AdminFeedbackItem {
  return {
    id: raw.id,
    materialId: raw.materialid,
    materialTitle: raw.materialtitle,
    agentId: raw.agentid,
    agentName: raw.agentname,
    agentEmail: raw.agentemail,
    content: raw.content,
    feedbackType: raw.feedbacktype,
    status: raw.status,
    submitTime: raw.submittime,
    processTime: raw.processtime,
    processBy: raw.processby,
    processByName: raw.processbyname,
    processRemark: raw.processremark,
  };
}

const FEEDBACK_TYPE_LABELS_ZH: Record<FeedbackType, string> = {
  ERRATA: '勘误',
  SUGGESTION: '修改建议',
  OTHER: '其他',
};

export function feedbackTypeLabelZh(type: FeedbackType): string {
  return FEEDBACK_TYPE_LABELS_ZH[type] ?? type;
}

export async function listFeedback(params: ListFeedbackParams): Promise<{
  list: AdminFeedbackItem[];
  total: number;
  pageNumber: number;
  pageSize: number;
}> {
  const p = new URLSearchParams();
  if (params.pageNumber) p.append('pageNumber', String(params.pageNumber));
  if (params.pageSize) p.append('pageSize', String(params.pageSize));
  if (params.status != null) p.append('status', String(params.status));
  if (params.materialId) p.append('materialId', params.materialId);
  if (params.agentId) p.append('agentId', params.agentId);
  if (params.keyword) p.append('keyword', params.keyword);
  if (params.startTime != null) p.append('startTime', String(params.startTime));
  if (params.endTime != null) p.append('endTime', String(params.endTime));
  const res = await apiClient.post('/agentmaterialfeedback/listFeedback.do', p);
  const d = res.data;
  if (d?.success) {
    const raw = d.data as {
      list: RawAdminFeedbackItem[];
      total: number;
      pageNumber: number;
      pageSize: number;
    };
    return {
      list: (raw.list ?? []).map(normalizeAdminFeedbackItem),
      total: raw.total,
      pageNumber: raw.pageNumber,
      pageSize: raw.pageSize,
    };
  }
  throw new Error(d?.info || '查询反馈列表失败');
}

export async function getFeedback(id: string): Promise<AdminFeedbackItem> {
  const p = new URLSearchParams();
  p.append('id', id);
  const res = await apiClient.post('/agentmaterialfeedback/getFeedback.do', p);
  const d = res.data;
  if (d?.success) {
    return normalizeAdminFeedbackItem(d.data as RawAdminFeedbackItem);
  }
  throw new Error(d?.info || '获取反馈详情失败');
}

export async function updateFeedbackStatus(params: {
  id: string;
  status: 0 | 1;
  processRemark?: string;
}): Promise<void> {
  const p = new URLSearchParams();
  p.append('id', params.id);
  p.append('status', String(params.status));
  if (params.processRemark) p.append('processRemark', params.processRemark);
  const res = await apiClient.post('/agentmaterialfeedback/updateFeedbackStatus.do', p);
  const d = res.data;
  if (!d?.success) {
    throw new Error(d?.info || '更新状态失败');
  }
}
